import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Seo } from "@/components/common/Seo";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal, SectionLabel } from "@/components/common/Reveal";
import { HealthDisclaimer } from "@/components/common/HealthDisclaimer";
import { studio, faqs, supportTopics } from "@/data/site";
import { visibleApps, getApp } from "@/data/apps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors duration-300 placeholder:text-gray-600 focus:border-[var(--accent)]";
const fieldStyle = { background: "var(--ink-2)", border: "1px solid var(--line)" };

export default function Support() {
  const [params, setParams] = useSearchParams();
  const initial = getApp(params.get("app")) ? params.get("app") : visibleApps[0]?.slug;
  const [appId, setAppId] = useState(initial);
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "question",
    subject: "",
    message: "",
    device: "",
  });
  const [sending, setSending] = useState(false);

  const app = getApp(appId);
  const list = useMemo(
    () => faqs.filter((f) => f.appId === "all" || f.appId === appId),
    [appId],
  );

  const onAppChange = (value) => {
    setAppId(value);
    setParams({ app: value }, { replace: true });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/support/requests`, {
        ...form,
        app_id: appId,
        device: form.device || null,
      });
      toast.success("Request received", {
        description: `Reference ${data.ticket_ref}. We reply to ${form.email} within 2–3 business days.`,
      });
      setForm({ name: "", email: "", topic: "question", subject: "", message: "", device: "" });
    } catch (err) {
      toast.error("Could not send your request", {
        description: `Please email ${studio.supportEmail} instead.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Support"
        path="/support"
        description={`Get help with any tutinsapp app. FAQs, contact form and per-app email support — AuraHealth customer service is ${studio.supportEmail}.`}
      />
      <PageHeader
        label="Help centre"
        lines={["Support."]}
        intro="Choose the app you need help with, check the common questions, then send us a message if you still need a hand. We answer every request."
      >
        <div className="mt-10 flex flex-wrap items-center gap-4">
          {app?.supportEmail ? (
            <a
              href={`mailto:${app.supportEmail}`}
              data-testid="support-email-link"
              className="glass inline-flex items-center gap-3 rounded-full px-5 py-3 transition-colors duration-300 hover:border-[var(--accent)]"
            >
              <Mail className="h-4 w-4" style={{ color: "var(--accent)" }} aria-hidden />
              <span className="font-mono-x text-sm text-white">{app.supportEmail}</span>
              <span className="font-mono-x text-[10px] uppercase tracking-[0.16em] text-gray-500">
                {app.name}
              </span>
            </a>
          ) : (
            <p
              data-testid="support-email-pending"
              className="glass inline-flex items-center gap-3 rounded-full px-5 py-3 font-mono-x text-xs text-gray-400"
            >
              <Mail className="h-4 w-4" style={{ color: "var(--accent)" }} aria-hidden />
              Support address published at launch — use the form below
            </p>
          )}
          <p className="font-mono-x text-[11px] uppercase tracking-[0.16em] text-gray-500">
            Response time: 2–3 business days
          </p>
        </div>
      </PageHeader>

      <section className="pb-8">
        <div className="shell">
          <SectionLabel>Which app do you need help with?</SectionLabel>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="support-app-select">
            {visibleApps.map((a) => (
              <button
                key={a.id}
                type="button"
                data-testid={`support-app-option-${a.slug}`}
                aria-pressed={appId === a.slug}
                onClick={() => onAppChange(a.slug)}
                className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5"
                style={
                  appId === a.slug
                    ? { background: "var(--surface-2)", border: `1px solid ${a.accent}` }
                    : { background: "var(--surface)", border: "1px solid var(--line)" }
                }
              >
                <img src={a.icon} alt="" className="h-8 w-8 rounded-lg object-cover" />
                <span className={appId === a.slug ? "text-white" : "text-gray-400"}>{a.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" id="faq">
        <div className="shell grid gap-16 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <div>
            <SectionLabel>Frequently asked questions</SectionLabel>
            <Accordion
              type="single"
              collapsible
              className="mt-8"
              data-testid={`support-faq-${appId}`}
            >
              {list.map((f, i) => (
                <AccordionItem
                  key={`${f.appId}-${i}`}
                  value={`item-${i}`}
                  style={{ borderColor: "var(--line)" }}
                >
                  <AccordionTrigger
                    data-testid={`support-faq-trigger-${i}`}
                    className="text-left text-sm text-white hover:no-underline sm:text-base"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-gray-400">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {app && (
              <div className="mt-10 space-y-4">
                <p className="text-sm text-gray-500">
                  More about {app.name}:{" "}
                  <Link
                    to={`/apps/${app.slug}`}
                    data-testid="support-app-detail-link"
                    className="link-underline"
                    style={{ color: "var(--accent)" }}
                  >
                    app page
                  </Link>
                  {" · "}
                  <Link to="/privacy" className="link-underline text-gray-300">
                    privacy
                  </Link>
                  {" · "}
                  <Link to="/terms" className="link-underline text-gray-300">
                    terms
                  </Link>
                </p>
                {app.isHealthApp && <HealthDisclaimer compact testId="support-health-disclaimer" />}
              </div>
            )}
          </div>

          <Reveal>
            <form
              onSubmit={submit}
              data-testid="support-form"
              className="rounded-3xl p-6 sm:p-8"
              style={{ background: "var(--surface)", border: "1px solid var(--line)" }}
              noValidate={false}
            >
              <h2 className="font-display text-xl font-bold text-white sm:text-2xl">
                Send a request
              </h2>
              <p className="mt-2 text-sm text-gray-400">
                About <span className="text-white">{app?.name}</span>. All fields except device are
                required.
              </p>

              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="label mb-2 block">Name</span>
                  <input
                    data-testid="support-form-name"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={set("name")}
                    className={field}
                    style={fieldStyle}
                    placeholder="Your name"
                  />
                </label>
                <label className="block">
                  <span className="label mb-2 block">Email</span>
                  <input
                    data-testid="support-form-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    className={field}
                    style={fieldStyle}
                    placeholder="you@example.com"
                  />
                </label>
              </div>

              <label className="mt-4 block">
                <span className="label mb-2 block">Topic</span>
                <select
                  data-testid="support-form-topic"
                  value={form.topic}
                  onChange={set("topic")}
                  className={field}
                  style={fieldStyle}
                >
                  {supportTopics.map((t) => (
                    <option key={t.value} value={t.value} style={{ background: "#0e1016" }}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="mt-4 block">
                <span className="label mb-2 block">Subject</span>
                <input
                  data-testid="support-form-subject"
                  required
                  maxLength={160}
                  value={form.subject}
                  onChange={set("subject")}
                  className={field}
                  style={fieldStyle}
                  placeholder="Short summary"
                />
              </label>

              <label className="mt-4 block">
                <span className="label mb-2 block">Message</span>
                <textarea
                  data-testid="support-form-message"
                  required
                  rows={6}
                  maxLength={5000}
                  value={form.message}
                  onChange={set("message")}
                  className={`${field} resize-y`}
                  style={fieldStyle}
                  placeholder="Tell us what happened, and what you expected to happen."
                />
              </label>

              <label className="mt-4 block">
                <span className="label mb-2 block">Device (optional)</span>
                <input
                  data-testid="support-form-device"
                  maxLength={160}
                  value={form.device}
                  onChange={set("device")}
                  className={field}
                  style={fieldStyle}
                  placeholder="e.g. iPhone 14, iOS 18.2"
                />
              </label>

              <button
                type="submit"
                data-testid="support-form-submit"
                disabled={sending}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-[#08090d] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                style={{ background: "var(--accent)" }}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {sending ? "Sending…" : "Send request"}
              </button>

              <p className="mt-4 text-xs leading-relaxed text-gray-500">
                We use your message and email only to answer you. See our{" "}
                <Link to="/privacy" className="link-underline text-gray-300">
                  Privacy Policy
                </Link>
                .
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
